import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Col, Input, Modal, Row, Select, Spin, Steps, Switch } from 'antd';

import { constants } from 'helpers/constants';
import { functions } from 'helpers/functions';
import QueryString from 'query-string';
import Truncate from 'v2/components/Shared/TruncateText';
import { useCreateConnectAction } from 'v2/hooks/useCreateConnectAction';
import { useListConnectOptions } from 'v2/hooks/useListConnectOptions';
import { useListTriggers } from 'v2/hooks/useListTriggers';
import { connectService } from 'v2/services/connect';
import { run } from 'validations/ruleRunner';

import ProductSelectTable from './ProductSelectTable';
import Validations from './validations';
import { useActionState } from '../../contexts/ActionStateContext';
import { useConnectState } from '../../contexts/ConnectStateContext';
import { getOptionParametersQuery, getOptionsParametersShouldModify } from '../../helpers/actionOptionsHelpers';

const NewAction: React.FC = () => {
  const { currentConnect } = useConnectState();
  const {
    newAction,
    modalNewAction: modal,
    stepAction: step,
    optionsAction,
    setStepAction,
    previousStepAction: handleBack,
    setNewAction,
    closeNewActionModal,
    addOptionAction,
    resetOptionsAction,
    resetOptionAction
  } = useActionState();
  const [validationErrors, setValidationErrors] = useState<any>([]);

  const createActionMutation = useCreateConnectAction();
  const { triggers } = useListTriggers();
  const { options } = useListConnectOptions(currentConnect?.autoresponder_id);

  const prevNewActionRef = useRef<any>(newAction);

  const errorFor = useCallback(
    (field: any) => {
      return validationErrors[field] || '';
    },
    [validationErrors]
  );

  const isValid = useCallback(
    (field: any) => {
      return validationErrors[field] ? true : false;
    },
    [validationErrors]
  );

  const handleChangeField = useCallback(
    (field: any, value: any) => {
      const updated = functions.cloneDeep(newAction);
      updated.fields = updated.fields || {};
      updated.fields[field] = value;
      updated.options_parameters = updated.fields;
      setNewAction(updated);
    },
    [setNewAction, newAction]
  );

  const handleChange = useCallback(
    (field: any, value: any) => {
      const updated = functions.cloneDeep(newAction);
      updated[field] = value;
      setNewAction(updated);
    },
    [setNewAction, newAction]
  );

  const dispatchNewOptionParam = useCallback(
    async (autoResponderId: number, connectId: number, itemName: string, params: any) => {
      const keys = itemName.split('_');
      const toSearch = `${keys[0]}_${keys[1]}`;

      let paramsString = undefined;

      if (params && typeof params === 'object') {
        paramsString = QueryString.stringify(params);
      }

      const result = await connectService.getActionFields(autoResponderId, connectId, toSearch, paramsString);
      const fieldData = result?.data ?? result;

      addOptionAction(itemName, functions.cloneDeep(fieldData));
    },
    [addOptionAction]
  );

  const fillOptionsParametersList = useCallback(
    (action: any, optionsParametersShouldModify: any) => {
      const selectedOption =
        options && Array.isArray(options) && options.find((item: any) => item.id === action.option_id);

      if (!optionsParametersShouldModify || optionsParametersShouldModify.length === 0) {
        return;
      }

      if (!selectedOption || !selectedOption.parameters || !Array.isArray(selectedOption.parameters.campos)) {
        return;
      }

      Promise.all(
        selectedOption.parameters.campos.map(async (item: any) => {
          if (!optionsParametersShouldModify.includes(item.nome)) {
            return;
          }

          if (item.campo_carregar) {
            const params = getOptionParametersQuery(item, selectedOption, action);

            if (item.campo_carregar === 'tag') {
              item = { ...item, campo_carregar: undefined };
            }

            await dispatchNewOptionParam(currentConnect.autoresponder_id, currentConnect.id, item.nome, params);
            return;
          }

          await dispatchNewOptionParam(currentConnect.autoresponder_id, currentConnect.id, item.nome, undefined);
        })
      );
    },
    [options, currentConnect, dispatchNewOptionParam]
  );

  const isValidToNextStep = useCallback(() => {
    switch (step) {
      case 0:
        return newAction.description ? true : false;
      case 1:
        return newAction.trigger_id ? true : false;

      case 2: {
        let valid = true;
        const selectedOption =
          options && Array.isArray(options) && options.find((item: any) => item.id === newAction.option_id);

        if (selectedOption && selectedOption.parameters && Array.isArray(selectedOption.parameters.campos)) {
          selectedOption.parameters.campos.forEach((item: any) => {
            valid = newAction.fields[item.nome] ? valid : false;
          });
        }

        return valid;
      }

      case 3:
        return newAction.products_id && Array.isArray(newAction.products_id) && newAction.products_id.length;
      default:
        return false;
    }
  }, [step, newAction, options]);

  const closeModal = useCallback(() => {
    closeNewActionModal();
  }, [closeNewActionModal]);

  const handleNext = useCallback(() => {
    setStepAction(step + 1);
  }, [setStepAction, step]);

  const handleSaveNewAction = useCallback(async () => {
    try {
      setStepAction(4);
      const actionToSave = Object.assign({}, newAction);
      actionToSave.autoresponder_id = currentConnect.autoresponder_id;
      await createActionMutation.mutateAsync({ connectId: currentConnect.id, action: actionToSave });
      setStepAction(5);
    } catch (error) {
      toast.error(error);
      setStepAction(3);
    }
  }, [createActionMutation, newAction, currentConnect, setStepAction]);

  const canFillOptionParam = useCallback(
    (name: string): boolean => {
      const selectedOption =
        options && Array.isArray(options) && options.find((item: any) => item.id === newAction.option_id);
      const optionParam =
        selectedOption &&
        Array.isArray(selectedOption.parameters.campos) &&
        selectedOption.parameters.campos.find((item: any) => item.nome === name);

      const basicValidation = newAction && optionsAction && optionsAction[name] && Array.isArray(optionsAction[name]);

      if (basicValidation && !optionParam.campo_carregar) {
        return true;
      }

      const tags = name.split('_');
      const fieldKey = [tags[0] || '', optionParam.campo_carregar, tags[2] || '']
        .filter(value => value && value.length > 0)
        .join('_');

      const advancedValidation =
        newAction &&
        newAction.fields &&
        newAction.fields[fieldKey] &&
        optionsAction &&
        optionsAction[name] &&
        Array.isArray(optionsAction[name]);

      return advancedValidation;
    },
    [newAction, options, optionsAction]
  );

  useEffect(() => {
    const prevAction = prevNewActionRef.current;

    const optionHasUpdated = prevAction.option_id !== newAction.option_id;
    const actionOptionsHasUpdated = prevAction.options_parameters !== newAction.options_parameters || optionHasUpdated;
    const shouldUpdateOptionsParametersList =
      newAction && newAction.options_parameters && currentConnect && actionOptionsHasUpdated;

    if (shouldUpdateOptionsParametersList) {
      (async () => {
        if (optionHasUpdated) {
          resetOptionsAction();
        }

        const actionsParametersShouldModify = getOptionsParametersShouldModify(newAction, prevAction, options);

        actionsParametersShouldModify.forEach((item: any) => {
          resetOptionAction(item);
        });

        fillOptionsParametersList(newAction, actionsParametersShouldModify);

        setValidationErrors(run(newAction, Validations));
      })();
    }

    prevNewActionRef.current = newAction;
  }, [newAction, currentConnect, options, resetOptionsAction, resetOptionAction, fillOptionsParametersList]);

  const currentOptions = Array.isArray(options) ? options : [];
  const selectedOption = currentOptions.find((item: any) => item.id === newAction.option_id);

  return (
    <Modal
      open={modal}
      onCancel={closeModal}
      title={<span className='text-text-heading'>Adicionar ação</span>}
      width={640}
      footer={
        <div className='flex items-end justify-end gap-2'>
          <div>{step !== 4 && step !== 5 && <Button onClick={closeModal}>Cancelar</Button>}</div>
          <div className='flex gap-2'>
            {step !== 0 && step !== 4 && step !== 5 && (
              <Button onClick={handleBack}>
                <LeftOutlined />
                Voltar
              </Button>
            )}
            {step !== 4 && step < 5 && (
              <Button
                type='primary'
                onClick={step === 3 ? handleSaveNewAction : handleNext}
                disabled={!isValidToNextStep()}
              >
                {step === 3 ? 'Salvar' : 'Avançar'}
                {step !== 3 && <RightOutlined />}
              </Button>
            )}
            {step === 5 && (
              <Button type='primary' onClick={closeModal}>
                Fechar
              </Button>
            )}
          </div>
        </div>
      }
    >
      {currentConnect && currentConnect.image && (
        <div className='mb-4 flex items-center gap-3'>
          <div
            className='mr-3 flex h-8.75 min-w-20 bg-contain bg-center bg-no-repeat'
            style={{ backgroundImage: `url(${currentConnect?.image})` }}
          />
          <div>
            <div className='font-semibold'>{currentConnect?.name}</div>
            <span className='text-sm text-gray-500'>{currentConnect?.description}</span>
            <div className='mt-1 text-sm'>
              <strong>{currentConnect?.parameters[0]?.label}</strong>
            </div>
            <Truncate count={50} final='...'>
              {currentConnect?.parameters[0]?.value}
            </Truncate>
          </div>
        </div>
      )}

      <Steps
        current={step}
        className='mb-4! sm:mb-6!'
        items={[{ title: 'Descrição' }, { title: 'Se acontecer' }, { title: 'Faça' }, { title: 'Produtos' }]}
      />

      <div>
        {step === 0 ? (
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <div className='flex flex-col items-start'>
                <span className='mb-1'>{newAction.active ? 'Ativado' : 'Desativado'}</span>
                <Switch checked={newAction.active} onChange={(checked: boolean) => handleChange('active', checked)} />
              </div>
            </Col>
            <Col xs={24} md={18}>
              <div>
                <label className='mb-1 block text-sm'>Título da ação</label>
                <Input
                  placeholder='Digite o título da ação'
                  value={newAction.description}
                  status={isValid('description') ? 'error' : undefined}
                  onChange={(e: any) => handleChange('description', e.target.value)}
                />
                {isValid('description') && <span className='text-xs text-red-500'>{errorFor('description')}</span>}
              </div>
            </Col>
          </Row>
        ) : step === 1 ? (
          <div className='flex w-full min-w-75 flex-wrap'>
            <div className='w-full'>
              <label className='mb-1 block text-sm'>Se isso acontecer:</label>
              <Select
                className='w-full'
                value={newAction.trigger_id}
                onChange={(value: any) => handleChange('trigger_id', value)}
                status={isValid('trigger_id') ? 'error' : undefined}
                options={
                  triggers && Array.isArray(triggers)
                    ? triggers.map((item: any) => ({ value: item.id, label: item.name }))
                    : []
                }
              />
              {isValid('trigger_id') && <span className='text-xs text-red-500'>{errorFor('trigger_id')}</span>}
            </div>
          </div>
        ) : step === 2 ? (
          <div>
            <div className='mb-4 w-full'>
              <label className='mb-1 block text-sm'>Faça isso:</label>
              <Select
                className='w-full'
                value={newAction.option_id}
                onChange={(value: any) => handleChange('option_id', value)}
                status={isValid('option_id') ? 'error' : undefined}
                disabled={!(options && Array.isArray(options))}
                options={currentOptions.map((option: any) => ({ value: option.id, label: option.title }))}
              />
              {isValid('option_id') && <span className='text-xs text-red-500'>{errorFor('option_id')}</span>}
            </div>
            {newAction.trigger_id &&
              newAction.option_id &&
              selectedOption &&
              selectedOption.parameters &&
              Array.isArray(selectedOption.parameters.campos) &&
              selectedOption.parameters.campos.map((item: any) => {
                return item.tipo === 'list' || item.tipo === 'listdinamico' ? (
                  <div className='mb-4 w-full' key={`fieldAction-${item.nome}`}>
                    <label className='mb-1 block text-sm'>{item.label}</label>
                    <Select
                      className='w-full'
                      value={newAction.fields[item.nome]}
                      onChange={(value: any) => handleChangeField(item.nome, value)}
                      disabled={!canFillOptionParam(item.nome)}
                      placeholder={!canFillOptionParam(item.nome) ? 'Carregando...' : 'Selecione'}
                      options={
                        optionsAction && Array.isArray(optionsAction[item.nome])
                          ? optionsAction[item.nome].map((opt: any) => ({ value: opt.id, label: opt.name }))
                          : []
                      }
                    />
                    {item.tooltip && <span className='text-xs text-gray-500'>{item.tooltip}</span>}
                    {currentConnect &&
                      currentConnect.autoresponder_id &&
                      currentConnect.autoresponder_id === constants.MAUTIC_AUTORESPONDER_ID && (
                        <span className='mt-1 block text-xs text-gray-500'>
                          * Caso a listagem não esteja carregando, limpe o cache do seu Mautic ou procure ajuda na&nbsp;
                          <a href='https://mautic.org/support' rel='noopener noreferrer' target='_blank'>
                            página de suporte do Mautic.
                          </a>
                        </span>
                      )}
                  </div>
                ) : (
                  <div className='mb-4 w-full' key={`fieldAction-${item.nome}`}>
                    <label className='mb-1 block text-sm'>{item.label}</label>
                    <Input
                      value={newAction.fields[item.nome]}
                      onChange={(e: any) => handleChangeField(item.nome, e.target.value)}
                    />
                    {item.tooltip && <span className='text-xs text-gray-500'>{item.tooltip}</span>}
                  </div>
                );
              })}
          </div>
        ) : step === 3 ? (
          <div className='text-center'>
            <ProductSelectTable />
          </div>
        ) : step === 4 ? (
          <div className='flex items-center justify-center py-8'>
            <Spin />
            <span className='ml-3'>Por favor aguarde, estamos salvando sua nova ação.</span>
          </div>
        ) : step === 5 ? (
          <div className='text-center'>
            <div> Ação salva com sucesso. </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default NewAction;
